import React from "react";
import { useNavigate } from "react-router-dom";
import "./LandingPage.css";
import PromptDemoUI from './PromptDemoUI';
import { useAuth } from "../AuthContext";
import {
  Swords,
  Timer,
  Globe,
  Target,
  BarChart3,
  Trophy,
  TrendingUp,
  CheckCircle2,
  Sparkles,
  HelpCircle,
} from "lucide-react";

// ─── FAQ data ────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: "What is a Head-to-Head Prompt Battle?",
    a: "Two players compete head-to-head on the same challenge. Both submit prompts within the time limit. An AI judge scores both entries instantly — highest score wins. Results are ranked on the global leaderboard.",
  },
  {
    q: "How do the time limits work?",
    a: "Choose your format: 2-minute Blitz for fast reflexes, 5-minute Sprint for balanced play, or 10-minute Deep Dive for complex challenges. A countdown timer is always visible, and your entry is auto-submitted when time runs out.",
  },
  {
    q: "How is scoring calculated?",
    a: "Scores are based on accuracy (does the prompt solve the task?), efficiency (conciseness), creativity, and AI alignment. The higher score wins the battle, and wins boost your ELO rating — which sets your global rank.",
  },
  {
    q: "What is the World Leaderboard?",
    a: "A live global ranking of all players by ELO rating. It resets monthly so new challengers always have a shot at the top. Weekly sprints offer ranked events to shake up standings.",
  },
  {
    q: "Do I need coding skills?",
    a: "Not at all for most challenges. Coding-specific battles are clearly labelled. Text, reasoning, and creative prompt challenges are fully open to everyone.",
  },
  {
    q: "Can I cancel my plan anytime?",
    a: "Yes — upgrade, downgrade, or cancel whenever you want. No contracts, no hidden fees.",
  },
];

// ─── Pricing plans ────────────────────────────────────────────────────────────
const PLANS = [
  {
    name: "Free",
    tagline: "Get a feel for it",
    price: "$0",
    period: "/month",
    features: [
      "30 Solo Challenges / month",
      "10 Ranked Battles / month",
      "2-min & 5-min formats",
      "Full score breakdown",
      "ELO rating & leaderboard",
      "Session history",
    ],
    excluded: [
      "10-min Deep Dive format",
      "Challenge a friend",
    ],
    cta: "Start Free",
    ctaLink: "/challenges",
    highlight: false,
    badge: null,
  },
  {
    name: "Starter",
    tagline: "For people getting serious",
    price: "$7",
    period: "/month",
    features: [
      "200 Solo Challenges / month",
      "Unlimited Ranked Battles",
      "2-min & 5-min formats",
      "Full score breakdown",
      "ELO rating & leaderboard",
      "Session history",
    ],
    excluded: [
      "10-min Deep Dive format",
      "Challenge a friend",
    ],
    cta: "Get Starter",
    ctaLink: "/plans",
    highlight: false,
    badge: null,
  },
  {
    name: "Pro",
    tagline: "The full experience",
    price: "$15",
    period: "/month",
    features: [
      "500 Solo Challenges / month",
      "Unlimited Ranked Battles",
      "All formats incl. 10-min Deep Dive",
      "Full score breakdown",
      "ELO rating & leaderboard",
      "Session history",
      "Challenge a friend",
      "Performance trend analytics",
    ],
    excluded: [],
    cta: "Go Pro",
    ctaLink: "/plans",
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Premium",
    tagline: "For the top of the leaderboard",
    price: "$30",
    period: "/month",
    features: [
      "1400 Solo Challenges / month",
      "Unlimited Ranked Battles",
      "All formats incl. 10-min Deep Dive",
      "Full score breakdown",
      "ELO rating & leaderboard",
      "Session history",
      "Challenge a friend",
      "Performance trend analytics",
    ],
    excluded: [],
    cta: "Go Premium",
    ctaLink: "/plans",
    highlight: false,
    badge: null,
  },
];

// ─── Benefits list data ────────────────────────────────────────────────────────
const BENEFITS = [
  { icon: Swords, text: "Challenge players worldwide in Head-to-Head Battles" },
  { icon: Timer, text: "Compete in 2-min Blitz, 5-min Sprint, or 10-min Deep Dive" },
  { icon: Globe, text: "Climb the live World Leaderboard with every win" },
  { icon: Target, text: "Sharpen skills with AI-scored Solo Challenges" },
  { icon: TrendingUp, text: "ELO & scoring system tracks every prompt you write" },
  { icon: Trophy, text: "Earn badges, divisions, and seasonal trophies" },
];

// ─── Feature cards data ───────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: Swords,
    title: "Head-to-Head Battles",
    desc: "Challenge any player worldwide. Same task, same clock — highest AI score wins. Climb divisions from Bronze to Grandmaster.",
    action: "Find a Battle",
    path: "/battles",
  },
  {
    icon: Timer,
    title: "Timed Formats",
    desc: "Three battle formats: 2-min Blitz, 5-min Sprint, 10-min Deep Dive. Each rewards a different kind of prompt mastery.",
    action: "Choose Format",
    path: "/battles/formats",
  },
  {
    icon: Globe,
    title: "World Leaderboard",
    desc: "Live global rankings updated after every match. Monthly resets keep competition fresh. Weekly ranked events shake up the standings.",
    action: "View Leaderboard",
    path: "/leaderboard",
  },
  {
    icon: Target,
    title: "Solo Challenges",
    desc: "Practice at your own pace across Beginner → Expert tiers. AI scores every submission instantly with detailed improvement notes.",
    action: "Start Practicing",
    path: "/challenges",
  },
  {
    icon: BarChart3,
    title: "Scoring & ELO System",
    desc: "Every prompt is scored on accuracy, efficiency, and creativity. Battle wins boost your ELO rating. Track it all on your dashboard.",
    action: "See How It Works",
    path: "/scoring",
  },
  {
    icon: Trophy,
    title: "Awards & Divisions",
    desc: "Earn badges, unlock division tiers, and collect seasonal trophies. Your rank persists on a public profile other players can challenge.",
    action: "View Achievements",
    path: "/dashboard/achievements",
  },
];

// ─── Sub-components ────────────────────────────────────────────────────────────
function FeatureCard({ icon: Icon, title, desc, action, path, user, openAuthModal, navigate }) {
  return (
    <div className="arena-feature-card">
      <h3>
        <Icon className="arena-feature-icon" size={22} strokeWidth={2} />
        {title}
      </h3>
      <p>{desc}</p>
      <button
        className="arena-feature-btn"
        onClick={() => (user ? navigate(path) : openAuthModal("signup"))}
      >
        {action}
      </button>
    </div>
  );
}

function PricingCard({ plan, navigate, user, openAuthModal }) {
  const isProtectedRoute = plan.ctaLink === "/challenges" || plan.ctaLink === "/tasks";

  const handleClick = () => {
    if (isProtectedRoute && !user) {
      openAuthModal("signup");
      return;
    }
    navigate(plan.ctaLink);
    window.scrollTo(0, 0);
  };

  return (
    <div className={`arena-plan-card ${plan.highlight ? "arena-plan-card--featured" : ""}`}>
      {plan.badge && <div className="arena-plan-badge">{plan.badge}</div>}
      <h3 className="arena-plan-name">{plan.name}</h3>
      <p className="arena-plan-tagline">{plan.tagline}</p>
      <div className="arena-plan-price-row">
        <span className="arena-plan-price">{plan.price}</span>
        <span className="arena-plan-period">{plan.period}</span>
      </div>
      <ul className="arena-plan-features">
        {plan.features.map((f, i) => (
          <li key={i}>
            <span className="arena-check">✓</span> {f}
          </li>
        ))}
        {plan.excluded.map((f, i) => (
          <li key={`ex-${i}`} className="arena-plan-feature--off">
            <span className="arena-check arena-check--off">—</span> {f}
          </li>
        ))}
      </ul>
      <button className="arena-plan-cta" onClick={handleClick}>
        {plan.cta}
      </button>
    </div>
  );
}

function FAQCard({ faq }) {
  return (
    <div className="arena-faq-card">
      <h3 className="arena-faq-card-q">
        <HelpCircle className="arena-faq-card-icon" size={18} strokeWidth={2} />
        {faq.q}
      </h3>
      <p className="arena-faq-card-a">{faq.a}</p>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function LandingPage({ openAuthModal }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleStartTrial = () => {
    if (user) {
      navigate("/challenges");
    } else {
      openAuthModal("signup");
    }
  };

  return (
    <div className="arena-root">

      {/* ── Hero ── */}
      <PromptDemoUI user={user} openAuthModal={openAuthModal} navigate={navigate} />

      {/* ── Benefits ── */}
      <section className="arena-benefits">
        <div className="arena-benefits__particles">
          {[...Array(4)].map((_, i) => <div key={i} className="arena-particle" />)}
        </div>
        <div className="arena-container">
          <h2 className="arena-section-title">Why Choose Prompttar</h2>

          <div className="arena-benefits-grid">

            {/* LEFT — benefit list */}
            <div className="arena-benefits-side">
              <ul className="arena-benefit-list">
                {BENEFITS.map(({ icon: Icon, text }, i) => (
                  <li key={i}>
                    <Icon className="arena-benefit-icon" size={20} strokeWidth={2} />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* RIGHT — video + CTA below it */}
            <div className="arena-benefits-main">
              <div className="arena-video-frame">
                <video
                  className="arena-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                >
                  <source
                    src="https://ik.imagekit.io/otwsklwun/Prompttar%20Demo.mp4"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>

              <button className="arena-btn-primary arena-benefits-cta" onClick={handleStartTrial}>
                {user ? "Enter Battle Arena" : "Start Free — Battle Now"}
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* ── Features Grid ── */}
      <section className="arena-features" id="features">
        <div className="arena-container">
          <h2 className="arena-section-title">Master Prompt Engineering Through Battle</h2>
          <p className="arena-section-sub">
            Solo challenges to sharpen your skills. Head-to-head battles to prove them. A world leaderboard to own.
          </p>
          <div className="arena-features-grid">
            {FEATURES.map((f) => (
              <FeatureCard
                key={f.title}
                {...f}
                user={user}
                openAuthModal={openAuthModal}
                navigate={navigate}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="arena-howto">
        <div className="arena-container">
          <h2 className="arena-section-title">How It Works</h2>
          <p className="arena-section-sub">From sign-up to World #1 — here's the path</p>

          <div className="arena-howto-grid">
            <ol className="arena-steps">
              {[
                "Log in and pick a mode — Solo Challenge or Ranked Battle.",
                "Choose your time format: Blitz (2 min), Sprint (5 min), or Deep Dive (10 min).",
                "Read the challenge brief. Write the best prompt you can before the clock hits zero.",
                "Submit — the AI judge scores accuracy, creativity, and efficiency instantly.",
                "See your ELO change, compare against your opponent, and review the model answer.",
                "Wins boost your ELO rating and push you up the World Leaderboard. Every match updates your rating, win or lose.",
              ].map((step, i) => (
                <li key={i}>{step}</li>
              ))}
              <div className="arena-steps-footer">
                <Sparkles size={18} strokeWidth={2} />
                <span>Practice. Battle. Rank. Repeat — one prompt at a time.</span>
              </div>
            </ol>

            <div className="arena-info-card">
              <h3>
                <Target size={22} strokeWidth={2} style={{ marginRight: 8, verticalAlign: "-4px" }} />
                Why Battle Format?
              </h3>
              <p>
                Pressure makes you a better prompt engineer. Timed head-to-head battles force you to think fast, cut fluff, and communicate precisely — the exact skills that matter in real AI work.
              </p>
              <ul>
                {[
                  "Real opponents, real stakes, real growth",
                  "Instant AI judge — no waiting, no bias",
                  "Every prompt scored, win or lose",
                  "Monthly leaderboard resets keep it fair",
                ].map((item, i) => (
                  <li key={i}>
                    <CheckCircle2 size={16} strokeWidth={2} className="arena-info-check" />
                    {item}
                  </li>
                ))}
              </ul>
              <p>
                Whether you're a beginner grinding Solo Challenges or a veteran chasing a Grandmaster title, every submission makes you sharper.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Scoring System ── */}
      <section className="arena-scoring">
        <div className="arena-container">
          <h2 className="arena-section-title">The Scoring System</h2>
          <p className="arena-section-sub">Every prompt is judged across four dimensions</p>
          <div className="arena-scoring-grid">
            {[
              { label: "Accuracy", pct: "40%", desc: "Does the prompt solve the task correctly and completely?" },
              { label: "Efficiency", pct: "25%", desc: "Is the prompt concise without losing clarity?" },
              { label: "Creativity", pct: "20%", desc: "Does the prompt use smart framing, examples, or constraints?" },
              { label: "AI Alignment", pct: "15%", desc: "How well does the prompt guide the model's behaviour?" },
            ].map((s) => (
              <div key={s.label} className="arena-score-card">
                <div className="arena-score-pct">{s.pct}</div>
                <div className="arena-score-name">{s.label}</div>
                <p className="arena-score-desc">{s.desc}</p>
              </div>
            ))}
          </div>
          <p className="arena-scoring-note">
            Battle wins boost your <strong>ELO rating</strong> beyond the base score. Division tiers (Bronze → Silver → Gold → Platinum → Diamond → Grandmaster) are determined by your ELO rating. Monthly resets move everyone to the nearest lower tier, keeping competition alive.
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      

      {/* ── Pricing ── */}
      <section className="arena-pricing">
        <div className="arena-container">
          <h2 className="arena-section-title">Choose Your Plan</h2>
          <p className="arena-section-sub">Everything core is free. Pay when you want more volume or the full format set.</p>
          <div className="arena-pricing-grid">
            {PLANS.map((plan) => (
              <PricingCard key={plan.name} plan={plan} navigate={navigate} user={user} openAuthModal={openAuthModal} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="arena-faq">
        <div className="arena-container">
          <h2 className="arena-section-title">Frequently Asked Questions</h2>
          <div className="arena-faq-grid">
            {FAQS.map((faq, i) => (
              <FAQCard key={i} faq={faq} />
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}